import React, { useState } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { Badge } from './ui/badge';
import { Eye, MessageSquare, User, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * QuoteCommentsPanel - A reusable component to show all comments/remarks for a quote
 * 
 * Usage: 
 * <QuoteCommentsPanel quoteId="xxx" quoteNo="01730/25-26" />
 * 
 * Shows an eye icon that when clicked opens a side panel with all comments
 */
export default function QuoteCommentsPanel({ quoteId, quoteNo }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState([]);

  const fetchComments = async () => {
    if (!quoteId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      fetchComments();
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getSourceColor = (source) => {
    if (source?.includes('Fund Request')) return 'bg-blue-100 text-blue-800';
    if (source?.includes('Material Request')) return 'bg-purple-100 text-purple-800';
    if (source?.includes('Payment')) return 'bg-green-100 text-green-800';
    if (source?.includes('Billing')) return 'bg-orange-100 text-orange-800';
    if (source?.includes('Rejection') || source?.includes('Rejected')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetTrigger asChild>
        <button
          className="inline-flex items-center justify-center p-1 rounded-full hover:bg-gray-100 transition-colors ml-1"
          title="View all comments/remarks for this quote"
          data-testid={`view-comments-${quoteId}`}
        >
          <Eye className="h-4 w-4 text-gray-500 hover:text-blue-600" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-[450px] sm:w-[500px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            Quote Comments & Remarks
          </SheetTitle>
          <p className="text-sm text-muted-foreground">Quote: {quoteNo}</p>
        </SheetHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500">Loading comments...</span>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No comments or remarks yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Comments from fund requests, material requests, and other actions will appear here.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground mb-4">
                {comments.length} comment{comments.length !== 1 ? 's' : ''} found
              </p>
              {comments.map((comment, index) => (
                <div
                  key={comment.id || index}
                  className="p-4 rounded-lg border bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge className={`text-xs ${getSourceColor(comment.source)}`}>
                      {comment.source}
                    </Badge>
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(comment.created_at)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap break-words">
                    {comment.comment}
                  </p>
                  
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                    <User className="h-3 w-3 text-gray-400" />
                    <span className="text-xs text-gray-500">
                      {comment.user_name}
                    </span>
                    <Badge variant="outline" className="text-xs ml-auto">
                      {comment.user_role}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
